import React, {useCallback, useEffect, useState} from "react";
import {
    Alert,
    Box,
    Card,
    CardContent,
    CircularProgress,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from "@mui/material";
import moment from "moment";
import dashboardServices from "app/services/dashboard-services";

// El panel de la tarjeta "Por preparar".
//
// Una sola pregunta: que se planifico y todavia no registra preparacion, y hace
// cuanto que espera. El reloj de cada fila arranca cuando el pedido entro al viaje
// -no cuando se creo el pedido- y la fila desaparece cuando el pedido pasa a
// pickeado, en progreso, cancelado o eliminado. La regla completa vive en
// Dashboard::PreparationFunnel.
//
// La tabla va del mas viejo al mas nuevo a proposito: arriba queda por donde hay
// que empezar. Las lista todas -no un top diez- con la cabecera fija y scroll
// propio: cortarla en diez dejaba al resto sin ninguna pantalla donde verse.
const TONO = "#B98A2E";

// Con la espera medida desde la planificacion, lo normal son horas; pero un pedido
// que quedo olvidado dos dias no se lee bien como "53 h".
const formatearEspera = (horas) => {
    if (horas === null || horas === undefined) return "—";
    if (horas < 24) return `${horas} h`;

    const dias = Math.floor(horas / 24);
    const resto = horas % 24;
    return resto === 0 ? `${dias} d` : `${dias} d ${resto} h`;
};

const PreparationFunnel = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [dia, setDia] = useState(moment().format("YYYY-MM-DD"));

    const cargar = useCallback(() => {
        setLoading(true);
        setError(null);

        dashboardServices
            .getPreparationFunnel({desde: dia, hasta: dia})
            .then((res) => setData(res))
            .catch(() => setError("No se pudo cargar la preparación del día."))
            .finally(() => setLoading(false));
    }, [dia]);

    useEffect(() => {
        cargar();
    }, [cargar]);

    const total = data?.total || 0;
    const filas = data?.oldest || [];

    return (
        <Card sx={{mb: 3}}>
            <CardContent>
                <Stack
                    direction={{xs: "column", md: "row"}}
                    justifyContent="space-between"
                    alignItems={{xs: "flex-start", md: "center"}}
                    spacing={1.5}
                    sx={{mb: 2}}
                >
                    <Stack spacing={0.5}>
                        <Typography variant="h4" sx={{mb: 0}}>Por preparar</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Pedidos que fueron planificados y aún no registran el proceso de preparación.
                        </Typography>
                    </Stack>

                    <TextField
                        id="preparation-funnel-dia"
                        label="Día"
                        type="date"
                        size="small"
                        value={dia}
                        onChange={(e) => setDia(e.target.value)}
                        InputLabelProps={{shrink: true}}
                    />
                </Stack>

                {loading && (
                    <Box sx={{display: "flex", justifyContent: "center", py: 6}}>
                        <CircularProgress/>
                    </Box>
                )}

                {!loading && error && <Alert severity="error">{error}</Alert>}

                {!loading && !error && total === 0 && (
                    <Alert severity="success">
                        No queda nada esperando preparación para este día.
                    </Alert>
                )}

                {!loading && !error && total > 0 && (
                    <>
                        <Stack direction="row" alignItems="baseline" spacing={1} sx={{mb: 2.5}}>
                            <Typography variant="h1" sx={{color: TONO, mb: 0}}>{total}</Typography>
                            <Typography variant="body2" color="text.secondary">
                                {total === 1 ? "pedido esperando" : "pedidos esperando"}
                            </Typography>
                        </Stack>

                        <Typography variant="h6" sx={{mb: 1}}>Las que están esperando</Typography>
                        <TableContainer sx={{maxHeight: 420}}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Pedido</TableCell>
                                        <TableCell>Cliente o lugar</TableCell>
                                        <TableCell>Viaje</TableCell>
                                        <TableCell align="right">Esperando</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filas.map((fila) => (
                                        <TableRow key={fila.delivery_id}>
                                            <TableCell>{fila.order_code || "—"}</TableCell>
                                            <TableCell>{fila.name || "—"}</TableCell>
                                            <TableCell>{fila.trip_code || "—"}</TableCell>
                                            <TableCell align="right">{formatearEspera(fila.hours_waiting)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {total > filas.length && (
                            <Typography variant="caption" color="text.secondary" sx={{display: "block", mt: 1}}>
                                Se muestran las {filas.length} que más llevan esperando, de {total}.
                                El resto está en la pantalla de pedidos.
                            </Typography>
                        )}
                    </>
                )}
            </CardContent>
        </Card>
    );
};

export default PreparationFunnel;
